import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useOAuthAPI } from '../../../api/use-oauth-api';
import { getSwrFetcherWithDynamicUrl } from '../../../utils/get-swr-fetcher-with-dynamic-url';
import { OAuthConnectionInformation } from '../../integrations/types/types';
import { DisconnectGmailResponse } from '../invoice-pay-page-inner-wrapper';

const DISCONNECT_XERO_URL = '/api/oauth/disconnect?provider=xero';
const XERO_CONNECTION_KEY = '/api/oauth/connections?provider=xero';

export const useXeroConnection = () => {
  const { checkXeroConnection } = useOAuthAPI();

  // Use SWR for shared cache state across all components
  const {
    data: xeroConnectionStatus,
    isLoading: isCheckingXeroConnection,
    mutate,
  } = useSWR<OAuthConnectionInformation>(
    XERO_CONNECTION_KEY,
    async () => {
      try {
        return await checkXeroConnection();
      } catch (error) {
        Sentry.captureException(error);
        return {
          connected: false,
          error: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    },
    {
      revalidateOnFocus: false, // Don't refetch on window focus
      revalidateOnReconnect: true, // Refetch on network reconnect
    },
  );

  const { trigger: triggerDisconnectXero, isMutating: isDisconnectingXero } = useSWRMutation<
    DisconnectGmailResponse,
    AxiosError,
    string,
    { urlSuffix: string }
  >(DISCONNECT_XERO_URL, getSwrFetcherWithDynamicUrl);

  const onDisconnectXero = async (connectionId: string) => {
    try {
      const result = await triggerDisconnectXero({ urlSuffix: `&connectionId=${connectionId}` });

      if (result.success) {
        // Invalidate SWR cache - all components using this hook will update immediately
        await mutate();
        toast.success('Xero disconnected successfully');
      }
    } catch (error) {
      Sentry.captureException(error);

      toast.error('Failed to disconnect Xero');
    }
  };

  return {
    xeroConnectionStatus: xeroConnectionStatus ?? null,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: {
      onDisconnectXero,
    },
  };
};
